import React from "react";

import { AdminCoverCropDetection } from "services";

import { ArrayUtils, getDocScanFieldResultKey, useGetFarmDocScanFields, useGetFieldPractices, useInView } from "utils";

import { CROP_TYPE_FCPO } from "models";

import { Col, Icon, Row, SkeletonWrapper, Typography } from "components";

import { FieldEvidenceSelection } from "pages/admin/verifications/components/automations/FieldEvidenceDrawer";
import { TimelineTileEvidence } from "pages/admin/verifications/components/automations/TimelineTileEvidence";
import { CoverCropStrip, MissingCoverCropTile } from "pages/admin/verifications/components/misc/CoverCropChips";
import { COVER_CROP_DEFAULT_MIN_CONFIDENCE, getCoverCropChip, getTimelineYears } from "pages/admin/verifications/utils/coverCropChips";
import { getDocScanEvidenceTitle, getDocScanEvidenceTone, getDocScanTileSignals } from "pages/admin/verifications/utils/docScanEvidence";
import { getEvidenceTriggerProps } from "pages/admin/verifications/utils/evidenceTrigger";
import { getWarnings } from "pages/admin/verifications/utils/warnings";

type CropTimelineProps = {
  fieldId: string;
  fieldName: string;
  farmId: string;
  coverCropDetections?: AdminCoverCropDetection[];
  minCoverCropConfidence?: number;
  onOpenEvidence: (selection: FieldEvidenceSelection) => void;
};

export const CropTimeline = ({
  fieldId,
  fieldName,
  farmId,
  coverCropDetections = [],
  minCoverCropConfidence = COVER_CROP_DEFAULT_MIN_CONFIDENCE,
  onOpenEvidence,
}: CropTimelineProps) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });
  const { data: docScanResults } = useGetFarmDocScanFields({ farmId });

  // Generate 7 years starting from current year - 5, including 2026
  const years = getTimelineYears();

  // Group crops by year
  const cropsByYear = ArrayUtils.groupBy(fieldPractices?.crops ?? [], (crop) => crop?.year ?? "unknown");

  // Define crop type order: MAIN -> COVER -> UNDERSOWING
  const cropTypeOrder = [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING];

  // Function to sort crops by type
  const sortCropsByType = (crops: NonNullable<typeof fieldPractices>["crops"]) =>
    crops.sort((a, b) => {
      const aIndex = cropTypeOrder.indexOf(a.cropType);
      const bIndex = cropTypeOrder.indexOf(b.cropType);
      return aIndex - bIndex;
    });

  const warningsByCrop = getWarnings(fieldPractices?.crops ?? []);

  return (
    <SkeletonWrapper isLoading={isLoading} type="block" height="9rem">
      <div ref={ref} className="practice-timeline">
        <Row>
          {/* prelint-ignore-col */}
          {years.map((year) => {
            const crops = cropsByYear?.[year] || [];
            const sortedCrops = sortCropsByType(crops);
            const detection = coverCropDetections.find(({ seasonYear }) => seasonYear === year);
            const coverCropChip = detection ? getCoverCropChip(detection, crops, minCoverCropConfidence) : null;
            // A verifier's No hides the tick. The drawer still gets the chip, so the answer can be changed.
            const visibleChip = coverCropChip?.detection.verifierIsCorrect === false ? null : coverCropChip;
            const docScanResult = docScanResults?.get(getDocScanFieldResultKey(fieldId, year));
            const openEvidence = (selection: Pick<FieldEvidenceSelection, "defaultTabKey" | "docScanFieldId">) =>
              onOpenEvidence({ fieldId, fieldName, year, coverCropWindowStart: detection?.obsWindowStart, coverCropChip, ...selection });
            const docScanCropId = docScanResult?.status ? sortedCrops.find(({ cropType }) => cropType === CROP_TYPE_FCPO.MAIN)?.id : undefined;
            return (
              <Col key={year} className="practice-timeline-year">
                <div className="practice-year-header">
                  <Typography variant="paragraph-s">{year}</Typography>
                </div>
                <div className="practice-year-content">
                  {sortedCrops.length === 0 ? (
                    <div className="practice-item practice-empty">No crops</div>
                  ) : (
                    sortedCrops.map((crop) => {
                      const strip = visibleChip?.cropId === crop.id ? visibleChip : null;
                      const hasDocScanEvidence = !!docScanResult && crop.id === docScanCropId;
                      // A verifier's Wrong hides the icons. The drawer still gets the result, so the answer can be changed.
                      const showDocScanStrip = hasDocScanEvidence && docScanResult.isCorrect !== false;
                      return (
                        <div
                          key={crop.id}
                          className={`practice-item ${crop.verificationStatus || "unknown"} ${warningsByCrop[crop.id]?.length ? "warning" : ""} ${
                            strip || showDocScanStrip ? "has-tile-strip" : ""
                          } has-evidence`}
                          title={`${crop.cropName} - ${crop.verificationStatus || "Unknown status"} ${warningsByCrop[crop.id]?.length ? "- has warning" : ""}`}
                          {...getEvidenceTriggerProps(`Show the ${hasDocScanEvidence ? "document" : "satellite"} evidence of ${crop.cropName} in ${year}`, () =>
                            openEvidence({ defaultTabKey: hasDocScanEvidence ? "docScan" : "remoteSensing", docScanFieldId: docScanResult?.scanFieldId })
                          )}
                        >
                          {strip && <CoverCropStrip chip={strip} />}
                          {showDocScanStrip && (
                            <TimelineTileEvidence
                              signals={getDocScanTileSignals(docScanResult)}
                              tone={getDocScanEvidenceTone(docScanResult)}
                              title={getDocScanEvidenceTitle(docScanResult)}
                            />
                          )}
                          <span className="practice-item-name">
                            {crop.cropName}
                            {crop.hasResidue && <Icon iconName="tabler/leaf" className="icon inline margin-left-25 green-text" size="s" />}
                          </span>
                        </div>
                      );
                    })
                  )}
                  {visibleChip && !visibleChip.cropId && (
                    <MissingCoverCropTile
                      chip={visibleChip}
                      triggerProps={getEvidenceTriggerProps(`Show the satellite evidence of the missing cover crop in ${year}`, () =>
                        openEvidence({ defaultTabKey: "remoteSensing" })
                      )}
                    />
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </SkeletonWrapper>
  );
};
